/**
 * @file src/pages/authentication-modal/authHooks.js
 * @description Signup-specific mutation hooks.
 *
 * What changed from the original:
 *  - All imports from utils/userApis → api/auth.api.js
 *  - Toast notifications kept — they are the right UX pattern here
 *    since SignupFormWithOTP does not have its own error display
 *  - Unused LoginUser import removed
 *  - No console.log
 *
 * Rules:
 *  - No Redux, no direct axios
 *  - No navigation — caller handles routing
 */

import { useMutation } from "@tanstack/react-query";
import { initiateSignup, verifyOtpAndCompleteSignup } from "api/auth.api";
import toast from "react-hot-toast";

// ---------------------------------------------------------------------------
// useInitiateSignup
// ---------------------------------------------------------------------------

/**
 * Sends OTP to the provided email to begin signup.
 * Displays toast on success and error.
 *
 * Usage:
 *  const { mutate, isPending } = useInitiateSignup();
 *  mutate({ email }, { onSuccess: () => setStep(2) });
 */
export const useInitiateSignup = () => {
  return useMutation({
    mutationFn: initiateSignup,
    onSuccess: (data) => {
      toast.success(data?.message ?? "Verification code sent to your email");
    },
    onError: (error) => {
      toast.error(error?.message ?? "Failed to send verification code");
    },
  });
};

// ---------------------------------------------------------------------------
// useVerifyOTP
// ---------------------------------------------------------------------------

/**
 * Verifies the OTP and completes the signup process.
 * Caller is responsible for navigation on success.
 *
 * Usage:
 *  const { mutate, isPending } = useVerifyOTP();
 *  mutate(signupData, { onSuccess: (response) => onSignupSuccess(response) });
 */
export const useVerifyOTP = () => {
  return useMutation({
    mutationFn: verifyOtpAndCompleteSignup,
    onSuccess: (data) => {
      toast.success(data?.message ?? "Account created successfully");
    },
    onError: (error) => {
      toast.error(error?.message ?? "Failed to verify code");
    },
  });
};
